import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Location, DOCUMENT as DOMDocument } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { MILESTONE_STATUS, MILESTONE_STATUS_LABEL } from "@program/shared/milestone-status.enum";
import { Installer } from "@project/installers/installer.model";
import { Offtaker } from "@project/offtakers/offtaker.model";
import { Project } from "@project/shared/project.model";
import { Document } from '@shared/components/files/shared/models/document.model';
import { Subscription } from "rxjs";
import { MilestoneStatus } from "../milestone-status.model";
import { MILESTONE_TYPE } from "../milestone-type.enum";
import { Milestone } from "../milestone.model";
import { MilestoneService } from "../milestone.service";
import { SideDrawerComponent } from "@shared/components/drawer/side-drawer.component";
import { UserService } from '@user/user.service';
import { MilestonePermissionsService } from '@shared/services/milestone-permissions.service';
import { MilestoneRowConfig } from '@project/detail/milestones/row/milestone-row-config';

@Component({
  selector: 'oes-milestone-row',
  templateUrl: './milestone-row.component.html',
  styleUrls: ['./milestone-row.component.scss'],
  providers: [MilestonePermissionsService]
})
export class MilestoneRowComponent implements OnInit, OnDestroy {
  @ViewChild('sideDrawer') sideDrawer!: SideDrawerComponent;

  @Input() index: number;
  @Input({ required: false }) config: MilestoneRowConfig;
  @Input() milestoneId: string;
  @Input() milestoneType: MILESTONE_TYPE;
  @Input() project: Project;
  @Input() projectInstaller: Installer;
  @Input() projectOfftaker: Offtaker;
  @Input() statusDropdownOptions: any[];

  documentsToDisplay: Document[];
  initialStatusSelection: any;
  formValid: boolean;
  milestone: Milestone = new Milestone({});
  milestoneForm: any;
  milestoneStatus: MilestoneStatus;
  milestoneTypes = MILESTONE_TYPE;
  milestoneViewAfterAnimation = false;
  milestoneViewShowing: boolean;
  readOnly: boolean;

  private deleteMilestoneSubscription: Subscription;
  private getMilestoneSubscription: Subscription;
  private programMilestoneDataSubscription: Subscription;
  private projectMilestoneDataSubscription: Subscription;

  private disableScrollSelector = '.milestones-container > oes-side-drawer > .drawer.open';
  private disableScrollClass = 'disable-scroll';
  private parentDrawerScroll: number;

  constructor(public milestonePermissionsService: MilestonePermissionsService,
              private _milestoneService: MilestoneService,
              private _activatedRoute: ActivatedRoute,
              private _router: Router,
              private _location: Location,
              private _userService: UserService,
              private _translateService: TranslateService,
              @Inject(DOMDocument) private document: globalThis.Document) {
    this.statusChanged = this.statusChanged.bind(this);

  }

  ngOnInit() {
    this.extractReadOnly();

    this.deleteMilestoneSubscription = this._milestoneService.deleteMilestone$.subscribe(_ => {
      this.milestoneViewShowing = false;
    });

    this.getMilestoneSubscription = this._milestoneService.getMilestoneByIdWithForm(this.milestoneId)
    .subscribe(({ milestone, form }) => {
      if (!milestone) {
        return;
      }
      this.milestone = milestone;
      this.milestoneForm = form;
      this.formValid = this.milestoneForm?.metadata?.valid;
      this.initialStatusSelection = this.findInitialStatusDropdownSelection(this.milestone.status);
      this.milestoneStatus = new MilestoneStatus(this.milestone.id, this.milestone.status);
    });
    this.documentsToDisplay = this.milestone.documents.filter(doc =>
      (doc.documentKey.docType as unknown) === 'FILE'
    );
    this._activatedRoute.queryParamMap.subscribe(params => {
      const routeMilestoneId = params.get('milestoneId');
      if (routeMilestoneId === this.milestoneId) {
        this.openMilestoneView();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.deleteMilestoneSubscription) {
      this.deleteMilestoneSubscription.unsubscribe();
    }
    if (this.programMilestoneDataSubscription) {
      this.programMilestoneDataSubscription.unsubscribe();
    }
    if (this.projectMilestoneDataSubscription) {
      this.projectMilestoneDataSubscription.unsubscribe();
    }
    if (this.getMilestoneSubscription) {
      this.getMilestoneSubscription.unsubscribe();
    }
  }

  get shouldHighlightMilestone(): boolean {
    return this.milestonePermissionsService.shouldHighlightMilestone(this.milestone);
  }

  extractReadOnly() {
    const projectPermissions = this.project?.projectPermissions;
    this.readOnly = projectPermissions?.project?.modulePermissions?.milestones?.readOnly;
  }

  statusChanged(event) {
    if (event[0].value && event[0].value !== this.milestone?.status) {
      this.milestone = new Milestone({ ...this.milestone, status: event[0].value });
      this._milestoneService.updateMilestone(this.milestone);
      this.milestoneStatus = new MilestoneStatus(this.milestone?.id, this.milestone?.status);
      this.initialStatusSelection = this.findInitialStatusDropdownSelection(this.milestone?.status);
      this.milestoneStatus = new MilestoneStatus(this.milestone?.id, this.milestone?.status);
    }
  }

  findInitialStatusDropdownSelection(name: MILESTONE_STATUS): any {
    const translatedName = this._translateService.instant(MILESTONE_STATUS_LABEL[name]);
    return [this.statusDropdownOptions.find(status => {
      return status.name === translatedName;
    })];
  }

  openMilestoneView() {
    this.milestoneViewShowing = true;
    this.milestoneViewAfterAnimation = true;
    const parentDrawer = this.document.querySelector(this.disableScrollSelector);
    if (parentDrawer) {
      parentDrawer.classList.add(this.disableScrollClass);
      this.parentDrawerScroll = parentDrawer.scrollTop;
      setTimeout(() => {
        this.sideDrawer.scrollChildIntoView();
      }, 500);
    }

    const currentUrl = this._router.createUrlTree([], {
      relativeTo: this._activatedRoute,
      queryParams: { milestoneId: this.milestoneId },
      queryParamsHandling: 'merge'
    }).toString();

    this._location.replaceState(currentUrl);
  }

  closeMilestoneView() {
    this.milestoneViewShowing = false;
    const parentDrawer = this.document.querySelector(this.disableScrollSelector);
    if (parentDrawer) {
      parentDrawer.classList.remove(this.disableScrollClass);
      parentDrawer.scrollTo({ top: this.parentDrawerScroll });
    }

    setTimeout(() => {
      this.milestoneViewAfterAnimation = false;
      const currentUrl = this._router.createUrlTree([], {
        relativeTo: this._activatedRoute,
        queryParams: {},
        queryParamsHandling: ''
      }).toString();
      this._location.replaceState(currentUrl);
    });
  }
}
